import AuthInput from "@/components/Auth/AuthInput";
import { authFieldGapSx } from "@/components/Auth/authSpacing";
import AuthPasswordInput from "@/components/Auth/AuthPasswordInput";
import AuthTextLink from "@/components/Auth/AuthTextLink";
import GoogleAuthButton from "@/components/Auth/GoogleAuthButton";
import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import { IS_BILLING } from "@/constants";
import useGoogleAuth from "@/hooks/useGoogleAuth";
import routes from "@/routes";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useLoginForm, { ILogin } from "./useLoginForm";

interface LoginFormProps {
  isAdmin: boolean;
}

export default function LoginForm({ isAdmin }: LoginFormProps) {
  const { control, onSubmit, isLoading, errMsg, resetError } =
    useLoginForm(isAdmin);
  const google = useGoogleAuth();
  const busy = isLoading || google.isLoading;
  // Only the most recent attempt's error is shown: each path clears the other's.
  const shownError = errMsg || google.errMsg;

  return (
    <>
      <Typography variant="h3Semi" component="h1" sx={{ color: "secondary.main" }}>
        {isAdmin ? "Admin sign in" : "Sign in"}
      </Typography>
      {shownError && <Alert severity="error">{shownError}</Alert>}
      {!isAdmin && (
        <GoogleAuthButton
          text="signin_with"
          disabled={busy}
          onCredential={(credential) => {
            resetError();
            google.onCredential(credential);
          }}
          onError={() => {
            resetError();
            google.onError();
          }}
        />
      )}
      <Box
        component="form"
        noValidate
        onSubmit={(e) => {
          google.reset();
          onSubmit(e);
        }}
        aria-busy={busy}
        sx={{ display: "flex", flexDirection: "column", ...authFieldGapSx }}
      >
        <AuthInput<ILogin>
          name="email"
          control={control}
          label="Email address"
          placeholder="jordan@acme.com"
          autoComplete="email"
          inputType="email"
        />
        <AuthPasswordInput<ILogin>
          name="password"
          control={control}
          label="Password"
          autoComplete="current-password"
        />
        <CustomLoadingButton
          type="submit"
          title="Sign In"
          isLoading={isLoading}
          disabled={busy}
          fullWidth
          sx={{ mt: 1, minHeight: 44, fontWeight: 600 }}
        />
      </Box>
      {!isAdmin && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2Reg" component="p">
            <AuthTextLink to={routes.FORGET_PASSWORD}>Forgot password?</AuthTextLink>
          </Typography>
          {IS_BILLING && (
            <Typography variant="body2Reg" component="p" sx={{ color: "text.secondary" }}>
              Don't have an account?{" "}
              <AuthTextLink to={routes.REGISTER}>Sign up</AuthTextLink>
            </Typography>
          )}
        </Box>
      )}
    </>
  );
}
